import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Mail, Clock } from 'lucide-react';

interface GeneralQuestionnaireThankYouScreenProps {
  testToken?: string;
}

const GeneralQuestionnaireThankYouScreen: React.FC<GeneralQuestionnaireThankYouScreenProps> = ({ testToken: propTestToken }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;

  useEffect(() => {
    console.log('All assessments completed with token:', testToken);
  }, [testToken]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          {/* Main Heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Assessment Completed Successfully!
          </h1>

          {/* Thank You Message */}
          <p className="text-xl text-gray-700 mb-8">
            Thank you for completing all parts of the assessment process.
          </p>

          {/* Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-indigo-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Mail className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">We'll Be In Touch</h3>
              <p className="text-sm text-gray-600">
                The Telerelation Team will review your responses and reach out to you shortly
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Clock className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">What's Next?</h3>
              <p className="text-sm text-gray-600">
                Our team is reviewing your responses and will contact you soon
              </p>
            </div>
          </div>

          {/* Detailed Message */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <p className="text-gray-700 leading-relaxed">
              You have successfully completed all three parts of the assessment:
            </p>
            <ul className="text-gray-700 leading-relaxed mt-3 list-disc list-inside space-y-2">
              <li>Initial Assessment Test</li>
              <li>Video Interview Test</li>
              <li>General Questionnaire</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Our recruitment team has been notified and will review your responses. 
              We appreciate the time you've taken to complete the assessment process. 
              Please keep an eye on your email for updates regarding the next steps in the recruitment process.
            </p>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-6">
            <p className="text-sm text-gray-500">
              If you have any questions, please don't hesitate to contact us at{' '}
              <a href="https://mail.google.com/mail/?view=cm&to=hr@telerelation.com" className="text-brand-500 hover:text-brand-600 font-medium">
                hr@telerelation.com
              </a>
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={() => window.location.href = "https://telerelation.com/"}
            className="mt-6 px-8 py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            Go to Website
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneralQuestionnaireThankYouScreen;
